import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { ProviderShortBadge } from '@root/entities/provider/ui'
import { FlashTextContentDistinct } from '@root/features/flash/flash-text-content-distinct'
import {
  CountMax3DigitsFormatted,
  HoldersCountFormatted,
  formatLiquidityBurned,
  NumericFormat,
} from '@root/shared/core/format'
import {
  LiquidityBurnedBoxIcon,
  ProfileIcon,
  UserGroupIcon,
} from '@root/shared/ui-kit'

import { PairExploreItemAuditDevHolding } from './audit/pair-explore-item-audit-dev-holding'
import { PairExploreItemAuditDevTokens } from './audit/pair-explore-item-audit-dev-tokens'
import { PairExploreItemAuditSnipers } from './audit/pair-explore-item-audit-snipers'
import { PairExploreItemAuditItem as Item } from './pair-explore-item-audit-item'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  pair: PairModel
  size?: number
  showProvider?: boolean
  showLpBurner?: boolean
} & ClassNameProps

export const PairExploreItemAudit = memo(
  ({
    className,
    size = 12,
    pair,
    showProvider = false,
    showLpBurner = false,
  }: Props) => {
    const { t } = useTranslation()
    const [audit] = useAtom(pair.audit)
    const [holdersCount] = useAtom(pair.holdersCount)

    const top10HoldersPercentage = useMemo(
      () =>
        NumericFormat.formatPreset(
          audit.checks.top10HoldersPercentage.percent,
          'percents',
          {
            mantissa: audit.checks.top10HoldersPercentage.percent >= 1 ? 0 : 1,
          },
        ),
      [audit.checks.top10HoldersPercentage.percent],
    )

    const lpBurned = audit.checks.lpBurned

    return (
      <Root className={className}>
        {showProvider ? <ProviderShortBadge pair={pair} /> : undefined}
        <PairExploreItemAuditDevHolding pair={pair} size={size} />
        <Tooltip
          content={t('trading.audit.top_10_holders_tooltip', {
            percentage: top10HoldersPercentage,
          })}
        >
          <Item pair={pair} intent={audit.checks.top10HoldersPercentage.status}>
            <ProfileIcon size={size} />{' '}
            {audit.checks.top10HoldersPercentage.amount > 0 ? (
              <FlashTextContentDistinct>
                {top10HoldersPercentage}%
              </FlashTextContentDistinct>
            ) : undefined}
          </Item>
        </Tooltip>

        <PairExploreItemAuditSnipers pair={pair} />
        <PairExploreItemAuditDevTokens pair={pair} size={size} />
        {showLpBurner ? (
          <Tooltip
            content={t('trading.audit.lp_burned.tooltip-percentage', {
              percents: `${formatLiquidityBurned(lpBurned.percent)}%`,
            })}
          >
            <Item pair={pair} intent={lpBurned.status}>
              <LiquidityBurnedBoxIcon height={size} />{' '}
              <FlashTextContentDistinct>
                {formatLiquidityBurned(lpBurned.percent)}%
              </FlashTextContentDistinct>
            </Item>
          </Tooltip>
        ) : undefined}

        <Tooltip
          content={t('trading.audit.holders_tooltip', {
            count: holdersCount,
          })}
        >
          <Item pair={pair}>
            <UserGroupIcon size={size} />
            {holdersCount ? (
              <FlashTextContentDistinct>
                <HoldersCountFormatted count={holdersCount} />
              </FlashTextContentDistinct>
            ) : undefined}
          </Item>
        </Tooltip>
      </Root>
    )
  },
)

const Root = styled.div`
  gap: 4px;

  display: grid;
  grid-auto-flow: column;
  justify-items: start;
  align-items: center;
  justify-content: start;
`
